import { Component } from '@theme/component';
import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { prefersReducedMotion } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';

const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
const MORPH_DURATION = 400;
const MORPH_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * Video Showcase section.
 *
 * Orchestrates the muted preview sequence across the video tiles and opens
 * the lightbox. The tiles live inside a nested `slideshow-component`, so refs
 * can't reach them from here — elements are queried by class instead.
 *
 * @typedef {object} Refs
 * @extends Component<Refs>
 */
class VideoShowcase extends Component {
  /** @type {HTMLElement[]} */
  #items = [];
  #hasVideos = false;
  #index = 0;
  #timer = 0;
  #inView = false;
  #lightboxOpen = false;
  #autoplayBlocked = false;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #mobile = window.matchMedia(MOBILE_MEDIA_QUERY);

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#collectItems();

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    document.addEventListener('visibilitychange', this.#sync, { signal });

    this.#lightbox?.addEventListener(DialogOpenEvent.eventName, this.#handleLightboxOpen, { signal });
    this.#lightbox?.addEventListener(DialogCloseEvent.eventName, this.#handleLightboxClose, { signal });

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#inView = Boolean(entry?.isIntersecting);
        this.#sync();
      },
      { threshold: 0.3 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  updatedCallback() {
    super.updatedCallback?.();

    // The theme editor re-renders the section via morph — re-resolve elements.
    this.#stop();
    this.#collectItems();
    this.#sync();
  }

  /** Preview duration per tile, in milliseconds. */
  get #duration() {
    const seconds = Number(this.getAttribute('preview-duration'));
    return (seconds > 0 ? seconds : 3) * 1000;
  }

  get #slideshow() {
    return this.querySelector('slideshow-component');
  }

  /** @returns {VideoShowcaseLightbox | null} */
  get #lightbox() {
    return this.querySelector('video-showcase-lightbox');
  }

  #collectItems() {
    this.#items = Array.from(this.querySelectorAll('.video-showcase-item'));
    this.#hasVideos = this.#items.some((item) => item.querySelector('video'));
    this.#index = 0;
  }

  /** Starts or stops the preview loop based on the current state. */
  #sync = () => {
    const shouldRun =
      this.#hasVideos &&
      this.#inView &&
      !document.hidden &&
      !this.#lightboxOpen &&
      !this.#autoplayBlocked &&
      !prefersReducedMotion();

    if (shouldRun && !this.#timer) {
      this.#play();
    } else if (!shouldRun) {
      this.#stop();
    }
  };

  /** Plays the preview for the current tile, then schedules the next one. */
  #play() {
    this.#stop();

    const item = this.#items[this.#index];
    if (!item) return;

    // Truthy sentinel so #sync can't double-start while play() is pending.
    this.#timer = -1;

    const video = item.querySelector('video');
    if (!video) {
      // Empty editor placeholder — skip to the next tile.
      this.#advance();
      return;
    }

    video.currentTime = 0;
    video
      .play()
      .then(() => {
        item.toggleAttribute('previewing', true);
        this.#timer = setTimeout(() => this.#advance(), this.#duration);
      })
      .catch(() => {
        // Autoplay rejected (e.g. iOS Low Power Mode) — fall back to posters.
        this.#autoplayBlocked = true;
        this.#stop();
      });
  }

  #advance() {
    this.#pauseCurrent();
    this.#index = (this.#index + 1) % this.#items.length;

    // On mobile the tiles are a slider — keep the visible slide in sync.
    if (this.#mobile.matches && this.#items.length > 1) {
      this.#slideshow?.select(this.#index);
    }

    this.#play();
  }

  #stop() {
    clearTimeout(this.#timer);
    this.#timer = 0;
    this.#pauseCurrent();
  }

  #pauseCurrent() {
    const item = this.#items[this.#index];
    const video = item?.querySelector('video');

    // Only pause if the video is still in its tile — when the lightbox opens,
    // the element has been reparented into the dialog and plays there.
    // Idle tiles rewind to the first frame instead of freezing mid-frame.
    if (video && item.contains(video)) {
      video.pause();
      video.currentTime = 0;
    }
    item?.removeAttribute('previewing');
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('.video-showcase-play');
    if (!button) return;

    const item = button.closest('.video-showcase-item');
    const video = item?.querySelector('video');
    if (!item || !video) return;

    event.preventDefault();

    const index = this.#items.indexOf(item);
    if (index >= 0) this.#index = index;

    this.#lightbox?.open(video, item, button);
  };

  /** @param {SlideshowSelectEvent} event */
  #handleSlideshowSelect = (event) => {
    // Manual swipe / arrow tap — restart the rhythm from the chosen slide.
    if (!event.detail?.userInitiated) return;

    this.#pauseCurrent();
    this.#index = event.detail.index ?? 0;
    if (this.#timer) this.#play();
  };

  #handleLightboxOpen = () => {
    this.#lightboxOpen = true;
    this.#sync();
  };

  #handleLightboxClose = () => {
    this.#lightboxOpen = false;
    this.#sync();
  };
}

if (!customElements.get('video-showcase')) {
  customElements.define('video-showcase', VideoShowcase);
}

/**
 * Lightbox for the Video Showcase section.
 *
 * Reuses the theme's DialogComponent (scroll lock, ESC, outside click, focus)
 * and adds a FLIP morph: the tile's own <video> element is reparented into
 * the dialog and animated from its tile position to the center — and back on
 * close. Auto-closes when the video ends.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} stage
 * @property {HTMLButtonElement} closeButton
 *
 * @extends DialogComponent<Refs>
 */
class VideoShowcaseLightbox extends DialogComponent {
  requiredRefs = ['dialog', 'stage'];

  /** @type {HTMLVideoElement | null} */
  #video = null;
  /** @type {HTMLElement | null} */
  #home = null;
  /** @type {HTMLElement | null} */
  #trigger = null;
  #closing = false;

  // DialogComponent defines closeDialog as an instance field, not a prototype
  // method, so `super.closeDialog()` can't reach it. Field initializers run in
  // order — capture the base implementation before our own field shadows it.
  #superCloseDialog = this.closeDialog;

  closeDialog = async () => {
    if (this.#closing || !this.refs.dialog.open) return;
    this.#closing = true;

    const video = this.#video;
    if (video) {
      video.removeEventListener('ended', this.#handleEnded);
      video.pause();
      video.controls = false;
      video.muted = true;
      await this.#morphToHome(video);
    }

    await this.#superCloseDialog();

    this.#trigger?.focus();
    this.#video = null;
    this.#home = null;
    this.#trigger = null;
    this.#closing = false;
  };

  /**
   * Opens the lightbox with the given tile video.
   *
   * @param {HTMLVideoElement} video - The tile's video element.
   * @param {HTMLElement} home - The tile the video returns to on close.
   * @param {HTMLElement} [trigger] - The play button, refocused on close.
   */
  open(video, home, trigger) {
    if (this.refs.dialog.open || this.#closing) return;

    this.#video = video;
    this.#home = home;
    this.#trigger = trigger ?? null;

    const from = video.getBoundingClientRect();
    this.refs.stage.appendChild(video);

    // DialogComponent dispatches dialog:open right after showModal() — the
    // stage has its final geometry there, which the FLIP morph needs.
    this.addEventListener(DialogOpenEvent.eventName, () => this.#enter(from), { once: true });
    this.showDialog();
  }

  /** @param {DOMRect} from - The tile rect captured before reparenting. */
  #enter(from) {
    const video = this.#video;
    if (!video) return;

    const to = video.getBoundingClientRect();
    if (!prefersReducedMotion() && to.width > 0 && from.width > 0) {
      const dx = from.left - to.left;
      const dy = from.top - to.top;
      const scale = from.width / to.width;

      video.animate(
        [{ transform: `translate(${dx}px, ${dy}px) scale(${scale})` }, { transform: 'none' }],
        { duration: MORPH_DURATION, easing: MORPH_EASING }
      );
    }

    video.currentTime = 0;
    video.muted = false;
    video.controls = true;
    // If unmuted playback is rejected, native controls are visible — the
    // visitor can press play manually.
    video.play().catch(() => {});
    video.addEventListener('ended', this.#handleEnded);
  }

  /** @param {HTMLVideoElement} video */
  async #morphToHome(video) {
    const home = this.#home;
    const to = home?.getBoundingClientRect();

    if (home && to && to.width > 0 && !prefersReducedMotion()) {
      const from = video.getBoundingClientRect();
      const dx = to.left - from.left;
      const dy = to.top - from.top;
      const scale = to.width / from.width;

      const animation = video.animate(
        [{ transform: 'none' }, { transform: `translate(${dx}px, ${dy}px) scale(${scale})` }],
        { duration: MORPH_DURATION, easing: MORPH_EASING, fill: 'forwards' }
      );
      await animation.finished.catch(() => {});
      home.prepend(video);
      animation.cancel();
    } else {
      home?.prepend(video);
    }

    // Back in its tile, the video idles on the first frame.
    video.currentTime = 0;
  }

  #handleEnded = () => {
    this.closeDialog();
  };
}

if (!customElements.get('video-showcase-lightbox')) {
  customElements.define('video-showcase-lightbox', VideoShowcaseLightbox);
}
